import { Link } from "react-router-dom";
import { trackTransportPublicEvent } from "../../../../hooks/useTransportPublicSeo";

export function TransportPublicHero({ hero, freshness }) {
  if (!hero) return null;
  return (
    <section className="transport-public-hero">
      <h1>{hero.title}</h1>
      <p>{hero.summary}</p>
      {hero.scoreBandLabel ? (
        <p>
          <span className="transport-public-chip">{hero.scoreBandLabel}</span>
          {hero.overallScore != null ? `Score ${Math.round(hero.overallScore)}` : null}
        </p>
      ) : null}
      {freshness?.label ? <p className="transport-public-disclaimer">{freshness.label}</p> : null}
    </section>
  );
}

export function TransportPublicMetrics({ metrics }) {
  if (!metrics) return null;
  const entries = [
    ["Mobility score", metrics.mobilityScore ?? "—"],
    ["Metro nearby", metrics.metroNearby ?? "—"],
    ["Bus stops nearby", metrics.busStopsNearby ?? "—"],
    ["Open reports", metrics.openMobilityReports ?? "—"],
  ];
  return (
    <section className="transport-public-section">
      <h2>Key metrics</h2>
      <div className="transport-public-grid">
        {entries.map(([label, value]) => (
          <div key={label} className="transport-public-stat">
            <div className="transport-public-stat-value">{value}</div>
            <div>{label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function TransportPublicAttribution({ attribution, methodology }) {
  return (
    <>
      {methodology ? (
        <section className="transport-public-section">
          <h2>Methodology</h2>
          <p>{methodology.summary}</p>
          <ul>
            {(methodology.limitations || []).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      ) : null}
      {attribution ? (
        <section className="transport-public-section">
          <h2>Source attribution</h2>
          <p className="transport-public-disclaimer">{attribution.mandatoryDisclaimer}</p>
          <ul className="transport-public-attribution-list">
            {(attribution.sources || []).map((source) => (
              <li key={source.key}>{source.label}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}

export function TransportPublicCtas({ ctas }) {
  if (!ctas) return null;
  const items = [
    ["exploreMap", ctas.exploreMap],
    ["viewProperties", ctas.viewProperties],
    ["submitReport", ctas.submitReport],
    ["requestReport", ctas.requestReport],
  ].filter(([, cta]) => cta?.href);

  return (
    <section className="transport-public-section">
      <h2>Next steps</h2>
      <div className="transport-public-cta-row">
        {items.map(([key, cta], index) => (
          <Link
            key={key}
            to={cta.href}
            className={index === 0 ? "" : "secondary"}
            onClick={() =>
              trackTransportPublicEvent("transport_public_cta_click", { ctaKey: key, href: cta.href })
            }
          >
            {cta.label}
          </Link>
        ))}
      </div>
    </section>
  );
}

export function TransportPublicReportsList({ mobilityRisks }) {
  const reports = mobilityRisks?.communityReports?.reports || [];
  if (!reports.length) {
    return (
      <section className="transport-public-section">
        <h2>Mobility reports</h2>
        <p className="transport-public-disclaimer">No public community mobility reports in this area.</p>
      </section>
    );
  }

  return (
    <section className="transport-public-section">
      <h2>Mobility reports</h2>
      <p className="transport-public-disclaimer">{mobilityRisks.disclaimer}</p>
      <div className="transport-public-report-list">
        {reports.slice(0, 8).map((report) => (
          <article key={report.reportKey || report.reportId} className="transport-public-report-card">
            <strong>{report.title || report.categoryLabel}</strong>
            <div>
              <span className="transport-public-chip">{report.categoryLabel}</span>
              <span className="transport-public-chip">{report.severity}</span>
              <span className="transport-public-chip">
                {report.isVerified ? "verified" : "unverified"}
              </span>
            </div>
            <p className="transport-public-disclaimer">{report.disclaimer}</p>
          </article>
        ))}
      </div>
    </section>
  );
}
